import Vue from 'vue';
import types from '../../mutation-types';

const setCompanyRecord = (records, company) => {
  Vue.set(records, company.id, {
    ...(records[company.id] || {}),
    ...company,
  });
};

const associationKeys = {
  contacts: 'contacts',
  relatedCompanies: 'relatedCompanies',
};

const associationItemId = item => String(item.id);

const upsertListItem = (list, item) => {
  const index = list.findIndex(record => associationItemId(record) === associationItemId(item));
  if (index === -1) return [...list, item];

  return list.map(record =>
    associationItemId(record) === associationItemId(item)
      ? { ...record, ...item }
      : record
  );
};

const deleteCompanyRecord = ($state, id) => {
  const index = $state.sortOrder.findIndex(item => String(item) === String(id));
  if (index !== -1) {
    Vue.delete($state.sortOrder, index);
  }
  Vue.delete($state.records, id);

  const walletIndex = $state.walletSortOrder.findIndex(
    item => String(item) === String(id)
  );
  if (walletIndex !== -1) {
    Vue.delete($state.walletSortOrder, walletIndex);
  }
  Vue.delete($state.walletRecords, id);
};

const normalizeLabelTitle = label => label.title || label.name || label;

const applyLabels = (currentLabels = [], labels = {}) => {
  const removeTitles = new Set((labels.remove || []).map(normalizeLabelTitle));
  const existingLabels = currentLabels.filter(
    label => !removeTitles.has(normalizeLabelTitle(label))
  );
  const existingTitles = new Set(existingLabels.map(normalizeLabelTitle));
  const labelsToAdd = (labels.add || [])
    .map(label => (typeof label === 'string' ? { title: label } : label))
    .filter(label => {
      const title = normalizeLabelTitle(label);
      if (!title || existingTitles.has(title)) return false;

      existingTitles.add(title);
      return true;
    });

  return [...existingLabels, ...labelsToAdd];
};

const applyCustomAttributes = (currentAttributes = {}, customAttributes = {}) => {
  const nextAttributes = {
    ...currentAttributes,
    ...(customAttributes.upsert || {}),
  };

  (customAttributes.remove || []).forEach(attributeKey => {
    delete nextAttributes[attributeKey];
  });

  return nextAttributes;
};

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

const applyCompanyBulkChanges = (company, changes = {}) => {
  const nextCompany = { ...company };

  if (hasOwn(changes, 'assignee')) {
    nextCompany.assignee = changes.assignee;
    nextCompany.assignee_id = changes.assignee?.id || null;
  }

  if (hasOwn(changes, 'team')) {
    nextCompany.team = changes.team;
    nextCompany.team_id = changes.team?.id || null;
  }

  if (changes.labels) {
    nextCompany.labels = applyLabels(nextCompany.labels, changes.labels);
    nextCompany.label_list = nextCompany.labels.map(normalizeLabelTitle);
  }

  if (changes.custom_attributes) {
    nextCompany.custom_attributes = applyCustomAttributes(
      nextCompany.custom_attributes,
      changes.custom_attributes
    );
  }

  return nextCompany;
};

export const mutations = {
  [types.SET_COMPANY_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },

  [types.SET_COMPANY_PROFILE_UI_FLAG]($state, data) {
    $state.profileUiFlags = {
      ...$state.profileUiFlags,
      ...data,
    };
  },

  [types.CLEAR_COMPANIES]($state) {
    Vue.set($state, 'records', {});
    Vue.set($state, 'sortOrder', []);
  },

  [types.CLEAR_COMPANY_WALLET]($state) {
    Vue.set($state, 'walletRecords', {});
    Vue.set($state, 'walletSortOrder', []);
  },

  [types.SET_COMPANY_META]($state, data = {}) {
    const { count = 0, current_page: currentPage = 1 } = data;
    Vue.set($state.meta, 'count', count);
    Vue.set($state.meta, 'currentPage', currentPage);
  },

  [types.SET_COMPANY_WALLET_META]($state, data = {}) {
    const { count = 0, current_page: currentPage = 1 } = data;
    Vue.set($state.walletMeta, 'count', count);
    Vue.set($state.walletMeta, 'currentPage', currentPage);
  },

  [types.SET_COMPANIES]($state, data = []) {
    const sortOrder = data.map(company => {
      setCompanyRecord($state.records, company);
      return company.id;
    });
    $state.sortOrder = sortOrder;
  },

  [types.SET_COMPANY_WALLET]($state, data = []) {
    const sortOrder = data.map(company => {
      setCompanyRecord($state.walletRecords, company);
      setCompanyRecord($state.records, company);
      return company.id;
    });
    $state.walletSortOrder = sortOrder;
  },

  [types.APPLY_COMPANY_BULK_CHANGES]($state, { companyIds = [], changes = {} }) {
    companyIds.forEach(companyId => {
      const company = $state.records[companyId];
      if (company) {
        setCompanyRecord($state.records, applyCompanyBulkChanges(company, changes));
      }

      const walletCompany = $state.walletRecords[companyId];
      if (walletCompany) {
        setCompanyRecord(
          $state.walletRecords,
          applyCompanyBulkChanges(walletCompany, changes)
        );
      }
    });
  },

  [types.SET_COMPANY_ITEM]($state, data) {
    setCompanyRecord($state.records, data);
    if (!$state.sortOrder.includes(data.id)) {
      $state.sortOrder.push(data.id);
    }
  },

  [types.SET_CURRENT_COMPANY]($state, data = {}) {
    Vue.set($state, 'currentCompany', data);
  },

  [types.SET_COMPANY_ASSOCIATIONS]($state, { type, data = [] }) {
    const key = associationKeys[type];
    if (!key) return;

    Vue.set($state.associations, key, data);
  },

  [types.ADD_COMPANY_ASSOCIATION]($state, { type, data }) {
    const key = associationKeys[type];
    if (!key || !data) return;

    Vue.set($state.associations, key, upsertListItem($state.associations[key], data));
  },

  [types.EDIT_COMPANY_ASSOCIATION]($state, { type, data }) {
    const key = associationKeys[type];
    if (!key || !data) return;

    Vue.set($state.associations, key, upsertListItem($state.associations[key], data));
  },

  [types.DELETE_COMPANY_ASSOCIATION]($state, { type, id }) {
    const key = associationKeys[type];
    if (!key) return;

    Vue.set(
      $state.associations,
      key,
      $state.associations[key].filter(item => associationItemId(item) !== String(id))
    );
  },

  [types.SET_COMPANY_NOTES]($state, { data: responseData = {}, page = 1 }) {
    const { meta = {}, payload = [] } = responseData;
    const currentPage = meta.current_page || 1;
    const totalPages = meta.total_pages || 0;
    const currentNotes = page === 1 ? [] : $state.notes.data;
    const newNotes = payload.filter(
      note => !currentNotes.find(currentNote => currentNote.id === note.id)
    );

    Vue.set($state, 'notes', {
      data: [...currentNotes, ...newNotes],
      meta: {
        currentPage,
        totalPages,
        totalCount: meta.total_count || payload.length,
        hasMore: currentPage < totalPages,
      },
    });
  },

  [types.ADD_COMPANY_NOTE]($state, data) {
    Vue.set($state.notes, 'data', upsertListItem($state.notes.data, data));
  },

  [types.EDIT_COMPANY_NOTE]($state, data) {
    Vue.set($state.notes, 'data', upsertListItem($state.notes.data, data));
  },

  [types.DELETE_COMPANY_NOTE]($state, noteId) {
    Vue.set(
      $state.notes,
      'data',
      $state.notes.data.filter(note => String(note.id) !== String(noteId))
    );
  },

  [types.SET_COMPANY_ACTIVITY_EVENTS]($state, { data: responseData = {} }) {
    const { meta = {}, payload = [] } = responseData;
    const currentPage = meta.current_page || 1;
    const totalPages = meta.total_pages || 0;

    Vue.set($state, 'activityEvents', {
      data: payload,
      meta: {
        currentPage,
        totalPages,
        perPage: meta.per_page || 10,
        totalCount: meta.total_count || payload.length,
        hasMore: currentPage < totalPages,
      },
    });
  },

  [types.SET_COMPANY_TASKS]($state, data = []) {
    Vue.set($state, 'tasks', data);
  },

  [types.ADD_COMPANY_TASK]($state, data) {
    Vue.set($state, 'tasks', upsertListItem($state.tasks, data));
  },

  [types.EDIT_COMPANY_TASK]($state, data) {
    Vue.set($state, 'tasks', upsertListItem($state.tasks, data));
  },

  [types.DELETE_COMPANY_TASK]($state, taskId) {
    Vue.set(
      $state,
      'tasks',
      $state.tasks.filter(task => String(task.id) !== String(taskId))
    );
  },

  [types.EDIT_COMPANY]($state, data) {
    setCompanyRecord($state.records, data);
    if ($state.walletRecords[data.id]) {
      setCompanyRecord($state.walletRecords, data);
    }
    if ($state.currentCompany.id === data.id) {
      Vue.set($state, 'currentCompany', {
        ...$state.currentCompany,
        ...data,
      });
    }
  },

  [types.DELETE_COMPANY]($state, id) {
    deleteCompanyRecord($state, id);
    if (String($state.currentCompany.id) === String(id)) {
      Vue.set($state, 'currentCompany', {});
    }
    $state.selectedCompanyIds = $state.selectedCompanyIds.filter(
      companyId => String(companyId) !== String(id)
    );
  },

  [types.SET_SELECTED_COMPANY_IDS]($state, ids = []) {
    $state.selectedCompanyIds = ids;
  },

  [types.CLEAR_SELECTED_COMPANY_IDS]($state) {
    $state.selectedCompanyIds = [];
  },

  [types.SET_COMPANY_FILTERS]($state, data) {
    $state.appliedFilters = data;
  },

  [types.CLEAR_COMPANY_FILTERS]($state) {
    $state.appliedFilters = [];
  },
};
